import { RoleScope } from '../../typings/enums';
import type { SelectOption } from '../types';
import { selectOptionFactory } from '../utils';
import type { RoleWithPermissions } from '../../shared/model-typings';

/**
 * Returns the label for a role.
 *
 * @param {Record<number, { name: string }>} roles - The roles to filter and sort.
 * @param {number | null} roleId - The ID of the role to get the label for.
 * @returns {string} The label for the role.
 */
export const roleLabel = (roles: { [key in number | string]: { name: string } }, roleId: number | null): string => {
  if (!roleId) {
    return 'None'; // null is not a number
  }

  return roles[roleId]?.name || 'None';
};

type RolesList = Array<{ id: number | null; name: string }>;

/**
 * Returns a list of roles for the organization.
 *
 * @param {Record<number, { name: string }>} roles - The roles to filter and sort.
 * @returns {Array<{ id: number | null; name: string }>} An array of roles.
 */
export const orgRolesList = (roles: { [key in number | string]: RoleWithPermissions }): RolesList => {
  const list: RolesList = Object.values(roles)
    .filter(role => role.scope === RoleScope.Organization)
    .sort((a, b) => a.name.localeCompare(b.name));

  list.push({
    id: null,
    name: 'None',
  });

  return list;
};

//  Sorting function for roles
const byRoleName = (a: RoleWithPermissions, b: RoleWithPermissions) => a.name.localeCompare(b.name);

//  Function to convert a role to a SelectOption object
const roleToSelectOption = (role: RoleWithPermissions) => selectOptionFactory(role.id, role.name);

/**
 * Takes an array of Roles, and a RoleScope,
 * and returns an array of SelectOption objects to be used in a selector component.
 *
 * @param {Array<RoleWithPermissions>} roles - The roles to filter and sort.
 * @param {RoleScope} scope - The scope of the roles to filter.
 * @returns {Array<SelectOption<number>>} An array of SelectOption objects.
 */
export const getRoleSelectOptions = (roles: RoleWithPermissions[], scope: RoleScope): Array<SelectOption<number>> =>
  roles
    ?.filter(role => role.scope === scope)
    .sort(byRoleName)
    .map(roleToSelectOption) ?? [];

/**
 * Takes an object of whos values are Roles, and a RoleScope,
 * and returns an array of SelectOption objects to be used in a selector component.
 *
 * @param {Record<number, RoleWithPermissions>} roles - The roles to filter and sort.
 * @param {RoleScope} scope - The scope of the roles to filter.
 * @returns {Array<SelectOption<number>>} An array of SelectOption objects.
 */
export const getRoleSelectOptionsFromObject = (
  roles: Record<number, RoleWithPermissions>,
  scope: RoleScope,
): Array<SelectOption<number>> =>
  getRoleSelectOptions(Object.values(roles), scope);
